import type { JSX } from 'solid-js';
import { t } from '../i18n';
import { claudeMcpAddCommand, MCP_DOCS_URL, mcpEndpoint, mcpJsonSnippet } from '../lib/mcp-connect';
import { mintersLabel } from '../lib/minters';
import { urlOrg } from '../lib/org';
import { CopySnippet } from './CopySnippet';
import { buttonClass } from './ui/Button';

// The connect-your-chat-client CONTENT, without a heading of its own — lifted
// out of `<ConnectMcpPanel/>` (arc 101 slice 04) when the onboarding funnel
// became its second consumer (epic #3629). Two consumers justify the split; one
// did not, which is why it lived inline until now — the `<ConnectMachineBody/>`
// precedent, for the same reason.
//
// THE HEADING IS THE CALLER'S, and that is the whole point of the extraction.
// The funnel's chat-client card is already titled "Connect a chat client"
// (`onboarding.step_mcp`, the same key its rail slot reads), and `connect_mcp.title`
// is that same sentence — so mounting the whole Panel inside that card rendered
// the identical `<h2>` twice, one directly under the other. A screen reader hears
// the heading twice and a sighted reader reads it twice; Playwright's strict mode
// is what named it.
//
// Everything the two sections say about OAuth leading and the PAT being the
// headless fallback is `<ConnectMcpPanel/>`'s docblock — this is its body, not a
// second place to state the rule.
//
// ONE CONNECTION, EVERY ACCOUNT (owner ruling 2026-10-28): every `tools/call`
// takes the reserved `account` argument (packages/mcp `act-as.ts`), so the
// accounts step names the page's org login as the value to pass. A connection
// authorized from an SSO-pinned session reaches only that org — the server
// refuses `account` with `sso_pinned` — so the step says to sign in with GitHub
// first when every account should be covered.

export function ConnectMcpBody(): JSX.Element {
  const endpoint = (): string => mcpEndpoint();
  const accountsDesc = (): string => {
    const org = urlOrg();
    return org === null
      ? t('connect_mcp.accounts_desc_any')
      : t('connect_mcp.accounts_desc', { login: org });
  };

  return (
    <>
      <p class="field__desc">{t('connect_mcp.intro')}</p>

      <section class="connect-mcp__step">
        <h3 class="connect-mcp__heading">{t('connect_mcp.oauth_title')}</h3>
        <p class="field__desc">{t('connect_mcp.oauth_desc')}</p>
        <CopySnippet label={t('connect_mcp.url_label')} snippet={endpoint()} />
      </section>

      <section class="connect-mcp__step">
        <h3 class="connect-mcp__heading">{t('connect_mcp.accounts_title')}</h3>
        <p class="field__desc">{accountsDesc()}</p>
        <p class="field__desc">{t('connect_mcp.accounts_sso')}</p>
      </section>

      <section class="connect-mcp__step">
        <h3 class="connect-mcp__heading">{t('connect_mcp.pat_title')}</h3>
        <p class="field__desc">{t('connect_mcp.pat_desc', { minters: mintersLabel() })}</p>
        <CopySnippet label={t('connect_mcp.cli_label')} snippet={claudeMcpAddCommand(endpoint())} />
        <CopySnippet label={t('connect_mcp.json_label')} snippet={mcpJsonSnippet(endpoint())} />
      </section>

      <a
        class={buttonClass({ variant: 'ghost', size: 'sm' })}
        href={MCP_DOCS_URL}
        target="_blank"
        rel="noreferrer"
      >
        {t('connect_mcp.docs')}
      </a>
    </>
  );
}
